import { useCallback, useEffect, useMemo, useState } from 'react'
import mergeRefs from 'react-merge-refs'
import type { ISODate } from '@sevenrooms/domain'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { useForm, useWatch } from '@sevenrooms/form'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly } from '@sevenrooms/timepiece'
import styled from '@sevenrooms/ui-kit'
import { FormCalendar } from '@sevenrooms/ui-kit/form'
import { useOverflowed, useThrottledResizeObserver } from '@sevenrooms/ui-kit/hooks'
import { Box, DividerLine, Flex, Modal, ModalBody, ModalHeader, VStack, Image } from '@sevenrooms/ui-kit/layout'
import { Header } from '@sevenrooms/ui-kit/typography'
import { ReservationAvailability } from '../../../components/PrimaryAvailability/ReservationAvailability'
import { RequestOrPriorityAlertButton } from '../../../components/RequestOrPriorityAlertButton'
import { DateStatus } from '../../../domain/dateStatus'
import {
  useExperienceValidDates,
  useLanguageStrings,
  useReservationsRoute,
  useSingleDayAvailability,
  useWidgetLanguage,
  useWidgetSettings,
} from '../../../hooks'
import { reservationWidgetMessages } from '../../../reservationWidgetMessages'
import { useModals } from '../../../store'
import { useAvailabilityDatesFormSchema } from './AvailabilityDatesModal.zod'
import { PartySizeSelector } from './PartySizeSelector'
import type { AvailabilityTimeWithUpSellCost, ExperienceWithAvailability } from '../../../hooks/useAvailability'
import type { ExperienceVenueInfo } from '@/Reservations/Search/ExperienceCard'

export interface AvailabilityDatesModalProps {
  experience: ExperienceWithAvailability
  venueInfo: ExperienceVenueInfo
  onStartDateChange?: (value: Date) => void
  partySize: number
  startDate?: string
  onPartySizeChange?: (value: number) => void
  onClose?: () => void
  clientId?: string
  onTimeSlotClick?: (timeSlot: AvailabilityTimeWithUpSellCost) => void
  onRequestBtnClick?: (requestDate: string, requestPartySize: number) => void
  onPriorityAlertBtnClick?: (requestDate: string, requestPartySize: number) => void
}

const imageHeight = '168px'

export function AvailabilityDatesModal({
  experience,
  venueInfo,
  onClose,
  onStartDateChange,
  partySize,
  startDate,
  onPartySizeChange,
  clientId,
  onTimeSlotClick,
  onRequestBtnClick,
  onPriorityAlertBtnClick,
}: AvailabilityDatesModalProps) {
  const { formatMessage } = useLocales()
  const { channel } = useReservationsRoute()
  const { isFetching: isFetchingLanguage } = useLanguageStrings()
  const { urlKey: experienceUrlKey, name: label, heroImage, offerType } = experience
  const { id: venueId, urlKey: venueUrlKey, venueToday, startOfDayTime, currencyCode, availabilityCacheDays } = venueInfo
  const { minGuests, maxGuests, defaultSearchTime } = useWidgetSettings()
  const { selectedLanguage } = useWidgetLanguage()
  const { showErrorModal } = useModals()
  const isPrivateEventsExperience = offerType === 'PDR'
  const [selectedPartySize, setSelectedPartySize] = useState(partySize)

  const viewAllDatesSchema = useAvailabilityDatesFormSchema()
  const { field, setValue } = useForm(viewAllDatesSchema)

  const selectedStartDate = useWatch(field.prop('startDate'))
  const selectedStartDateIso = DateOnly.fromJsDateSafe(selectedStartDate)?.toIso()

  const onAvailabilityClick = useCallback(
    (timeSlot: AvailabilityTimeWithUpSellCost) => {
      onPartySizeChange?.(selectedPartySize)
      onStartDateChange?.(selectedStartDate)
      onTimeSlotClick?.(timeSlot)
    },
    [onPartySizeChange, onStartDateChange, onTimeSlotClick, selectedPartySize, selectedStartDate]
  )

  const {
    isFetching: isAvailabilityFetching,
    errorCode,
    isClosed,
    data: availabilityData,
  } = useSingleDayAvailability({
    venueId,
    venue: venueUrlKey,
    startDate: selectedStartDateIso as string, // always string due to skipRequests param
    partySize: selectedPartySize,
    startTime: ReservationWidget.AllTimesOption,
    selectedLangCode: selectedLanguage,
    haloTimeIntervalMinutes: ReservationWidget.HaloMinutesOptions[ReservationWidget.HaloMinutesOptions.length - 1] as number,
    channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
    haloSizeInterval: ReservationWidget.WholeDayHaloSizeInterval,
    timeSlot: defaultSearchTime,
    startOfDayTime,
    skipRequests: !selectedStartDateIso,
    clientId,
    experienceId: experienceUrlKey,
  })

  const noAvailabilityTitle = () => {
    if (isClosed && selectedStartDate) {
      return formatMessage(reservationWidgetMessages.resWidgetNoShiftsOnDate, {
        date: DateOnly.fromDate(selectedStartDate).formatSMonthNDaySWeek(),
      })
    }

    return formatMessage(reservationWidgetMessages.resWidgetNoAvailabilityCriteria)
  }

  const {
    data: { availability, firstAvailableDayForBooking },
    isLoading: isLoadingValidDays,
    isError: isErrorValidDates,
  } = useExperienceValidDates({
    partySize,
    selectedDate: startDate,
    experienceUrlKey,
    includeStartDate: true,
    venueUrlKey,
    venueToday,
    availabilityCacheDays,
    skipRequests: !partySize,
  })

  useEffect(() => {
    if (isErrorValidDates || errorCode !== undefined) {
      showErrorModal()
    }
  }, [errorCode, formatMessage, isErrorValidDates, showErrorModal])

  useEffect(() => {
    if (!isLoadingValidDays && firstAvailableDayForBooking) {
      setValue('startDate', new Date(firstAvailableDayForBooking))
    }
  }, [firstAvailableDayForBooking, isLoadingValidDays, setValue])

  const isDateDisabled = useCallback(
    (date: Date) => {
      if (isLoadingValidDays) {
        return true
      }
      const isoDate = DateOnly.fromDate(date).toIso()
      const dayStatus = availability?.[isoDate as ISODate]?.[selectedPartySize]
      return dayStatus === undefined || dayStatus === DateStatus.NOT_AVAILABLE
    },
    [availability, isLoadingValidDays, selectedPartySize]
  )

  const isDateInactive = useCallback(
    (date: Date) => {
      if (isLoadingValidDays) {
        return true
      }
      const isoDate = DateOnly.fromDate(date).toIso()
      const dayStatus = availability?.[isoDate as ISODate]?.[selectedPartySize]
      return dayStatus === DateStatus.REQUESTABLE
    },
    [availability, isLoadingValidDays, selectedPartySize]
  )

  const { ref: resizeRef, height: wrapperHeight } = useThrottledResizeObserver(50, 'border-box')
  const [isOverflowed, overflowRef] = useOverflowed(wrapperHeight)
  const showTopDividerLine = isOverflowed || !label

  const requestOrPriorityAlertAvailability = useMemo(() => {
    const requestTimeRange = availabilityData?.requestTimeRange
    const reservationTimesInHalo = availabilityData?.reservationTimesInHalo

    if (requestTimeRange?.length && partySize && selectedStartDateIso) {
      return (
        <RequestOrPriorityAlertButton
          isPrivateEvent={isPrivateEventsExperience}
          buttonVariant={reservationTimesInHalo?.length === 0 ? 'primary' : 'secondary'}
          onRequestButtonClick={() => onRequestBtnClick?.(selectedStartDateIso, selectedPartySize)}
          onPriorityAlertButtonClick={() => onPriorityAlertBtnClick?.(selectedStartDateIso, selectedPartySize)}
          partySize={partySize}
        />
      )
    }
    return null
  }, [
    availabilityData?.requestTimeRange,
    availabilityData?.reservationTimesInHalo,
    partySize,
    selectedStartDateIso,
    isPrivateEventsExperience,
    onRequestBtnClick,
    selectedPartySize,
    onPriorityAlertBtnClick,
  ])

  return (
    <Modal ariaLabel="Modal" width="100%" maxWidth="600px" maxHeight="90%" data-test="sr-experience-details-modal">
      <ModalHeader position="relative" boxShadow={isOverflowed ? 'tertiary' : 'none'} onClose={() => onClose?.()}>
        <Flex width="100%" justifyContent="center">
          <Header type="h2" data-test="modal-title">
            {label}
          </Header>
        </Flex>
      </ModalHeader>
      {showTopDividerLine && <DividerLine margin="none" />}
      <ModalBody ref={mergeRefs([overflowRef, resizeRef])} maxWidth="412px" width="412px">
        <VStack mt={showTopDividerLine ? 'lm' : 'none'} mb="lm" spacing="m">
          {heroImage && (
            <Box mb="lm" overflow="hidden" borderRadius="s" width="100%" minWidth="100%" height={imageHeight} minHeight={imageHeight}>
              <Image centered height="100%" width="100%" data-test="sr-room-card-image" src={heroImage} alt="room image" />
            </Box>
          )}
          <VStack spacing="s">
            <Box>
              <PartySizeSelector value={selectedPartySize} min={minGuests} max={maxGuests} onChange={setSelectedPartySize} />
            </Box>
            <StyledDatePicker
              dataTest="sr-experience-details-calendar"
              value={field.prop('startDate')}
              disabled={isDateDisabled}
              inactive={isDateInactive}
              locale={selectedLanguage}
            />
          </VStack>
          <ReservationAvailability
            isLoadingData={isAvailabilityFetching}
            isLoadingLanguage={isFetchingLanguage}
            onClick={onAvailabilityClick}
            timeslots={availabilityData?.reservationTimesInHalo || []}
            variant="primary"
            desktopColumnsCount={2}
            tabletColumnsCount={2}
            noAvailabilityTitle={noAvailabilityTitle()}
            currencyCode={currencyCode}
            requestOrPriorityAlertAvailability={requestOrPriorityAlertAvailability}
          />
        </VStack>
      </ModalBody>
    </Modal>
  )
}

const StyledDatePicker = styled(FormCalendar)`
  margin: ${props => props.theme.spacing.none};
  .rdp-table {
    width: 100%;
    max-width: 100%;
  }
  .rdp-month {
    width: 100%;
  }
`
